import React, { useCallback, useState } from 'react';
import { fg } from '@confluence/feature-gating';
import type { DocNode } from '@atlaskit/adf-schema';
import type { AvailableSite, IssueCreateEmbedSuccessPayload } from '@atlassian/gic-anywhere';
import { GicAnywhere } from '@atlassian/gic-anywhere';

import { SingleWorkCreateForm } from '../single-work-create-form/SingleWorkCreateForm';
import type { JiraProject } from '../../__types__/apiUtils';
import type { IssueType } from '../../providers/BulkCreateContextProvider';
import { useIssueCreateSidePanelActions } from '../../providers/IssueCreateSidePanelContextProvider';

export interface IssueCreateFormRendererProps {
	cloudId: string;
	onIssueCreateInit: () => void;
	onSuccess: (payload: IssueCreateEmbedSuccessPayload) => void;
	onClose: () => void;
	onFailure: (error: unknown) => void;
	summary?: string;
	descriptionAdf?: DocNode;
	overrideCloudIdForSitePicker?: string;
	isPayloadReady?: boolean;
	overrideProjectId?: string;
	setOverrideCloudIdForSitePicker: (cloudId: string) => void;
	setOverrideProjectId: (projectId: string) => void;
}

export const IssueCreateFormRenderer: React.FC<IssueCreateFormRendererProps> = ({
	cloudId,
	onIssueCreateInit,
	onSuccess,
	onClose,
	onFailure,
	summary,
	descriptionAdf,
	overrideCloudIdForSitePicker,
	isPayloadReady,
	overrideProjectId,
	setOverrideCloudIdForSitePicker,
	setOverrideProjectId,
}) => {
	const [shouldSwitchToGicAnywhere, setShouldSwitchToGicAnywhere] = useState(false);
	const actions = useIssueCreateSidePanelActions();

	const onShouldSwitchToGicAnywhere = useCallback(
		(details: {
			selectedSite: AvailableSite | null;
			selectedProject: JiraProject | null;
			selectedIssueType: IssueType | null;
			summary: string;
			description: DocNode;
		}) => {
			setShouldSwitchToGicAnywhere(true);
			if (details.selectedSite) {
				setOverrideCloudIdForSitePicker(details.selectedSite.cloudId);
			}
			if (details.selectedProject) {
				setOverrideProjectId(details.selectedProject.id);
			}
			if (details.summary) {
				void actions.setSummary(details.summary);
			}
			if (details.description) {
				void actions.setDescriptionAdf(details.description);
			}
		},
		[setOverrideCloudIdForSitePicker, setOverrideProjectId, actions],
	);

	const formToRender = shouldSwitchToGicAnywhere ? (
		<GicAnywhere
			cloudId={cloudId}
			consumer="confluence-highlight-issue-create"
			onInit={onIssueCreateInit}
			onSuccess={onSuccess}
			onClose={onClose}
			onFailure={onFailure}
			summary={summary}
			descriptionAdf={descriptionAdf}
			overrideCloudId={overrideCloudIdForSitePicker}
			isPayloadReady={isPayloadReady}
			projectId={overrideProjectId}
		/>
	) : (
		<SingleWorkCreateForm
			summary={summary}
			descriptionAdf={descriptionAdf}
			closePanel={onClose}
			onShouldSwitchToGicAnywhere={onShouldSwitchToGicAnywhere}
		/>
	);

	return fg('jira_ai_single_issue_create_panel_form') ? (
		formToRender
	) : (
		<GicAnywhere
			cloudId={cloudId}
			consumer="confluence-highlight-issue-create"
			onInit={onIssueCreateInit}
			onSuccess={onSuccess}
			onClose={onClose}
			onFailure={onFailure}
			summary={summary}
			descriptionAdf={descriptionAdf}
			overrideCloudId={overrideCloudIdForSitePicker}
			isPayloadReady={isPayloadReady}
			projectId={overrideProjectId}
		/>
	);
};
